import './ChannelPerformanceLine.scss';
import cn from 'classnames';
import { ReactNode, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Neutral500, Neutral600, Neutral900 } from '@src/utils/colors';
import {
  ChartTooltipV2,
  KeyShapeOptions,
  PayloadItem
} from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { useChannelColor, useDtcCommerceMmmData } from '../context/DtcCommerceMmmDataContext';
import { BASELINE_DISPLAY_NAME, BASELINE_TOGGLE_LABEL } from '../dtc-commerce-mmm-constants';
import {
  CPTBRow,
  DashboardData,
  HierarchyLevel,
  HierarchyLevelConst
} from '../dtc-commerce-mmm-types';
import { aggregateChannels } from '../hooks/useChannelAggregation';
import {
  formatCurrency,
  formatDollarsExact,
  formatPercentage,
  formatWholeNumber
} from '../utils/dtc-commerce-mmm-format';
import { findYearBoundaries } from '../utils/dtc-commerce-mmm-year-markers';
import { IncrementalityBeaker } from './IncrementalityBeaker';
import { IncrementalityNote } from './IncrementalityNote';
import {
  useElementWidth,
  YEAR_MARKER_STROKE,
  YearMarkerLabel
} from '../utils/dtc-commerce-mmm-year-marker-ui';

interface ChannelPerformanceLineProps {
  data: DashboardData;
  hierarchyLevel: HierarchyLevel;
}

const MetricView = {
  SPEND: 'spend',
  CONTRIBUTION: 'contribution',
  EFFICIENCY: 'efficiency'
} as const;
type MetricView = (typeof MetricView)[keyof typeof MetricView];

const TimeAgg = {
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly'
} as const;
type TimeAgg = (typeof TimeAgg)[keyof typeof TimeAgg];

const DisplayMode = {
  ACTUAL: 'actual',
  PERCENT: 'percent'
} as const;
type DisplayMode = (typeof DisplayMode)[keyof typeof DisplayMode];

const FADED_OPACITY = 0.1;
const NORMAL_FILL_OPACITY = 0.75;

// Isolation key for the blended line, which is a computed series rather than a channel. Prefixed
// so it can never collide with a real channel name coming from the model.
const BLENDED_SERIES_KEY = '__blended__';
const BLENDED_SERIES_LABEL = 'Blended';

const groupKeyFor = (row: CPTBRow, level: HierarchyLevel): string => {
  switch (level) {
    case HierarchyLevelConst.PLATFORM:
      return `${row.channel}|${row.platform}`;
    case HierarchyLevelConst.TACTIC:
      return `${row.channel}|${row.platform}|${row.tactic}`;
    case HierarchyLevelConst.BRAND_NONBRAND:
      return row.name;
    case HierarchyLevelConst.CHANNEL:
    default:
      return row.channel;
  }
};

const periodKey = (date: string, agg: TimeAgg): string => {
  if (agg === TimeAgg.WEEKLY) return date;
  const d = new Date(`${date}T00:00:00`);
  const year = d.getFullYear();
  const month = d.getMonth();
  if (agg === TimeAgg.MONTHLY) return `${year}-${String(month + 1).padStart(2, '0')}`;
  const quarter = Math.floor(month / 3) + 1;
  return `${year}-Q${quarter}`;
};

interface PeriodEntry {
  date: string;
  totalEfficiency: number | null;
  [key: string]: string | number | null;
}

interface TooltipEntry {
  name: string;
  value: number;
  color: string;
}

interface TooltipPayloadItem {
  name?: string;
  value?: number;
  color?: string;
  stroke?: string;
  payload?: PeriodEntry;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  metric: MetricView;
  timeAgg: TimeAgg;
  displayMode: DisplayMode;
  isRevenue: boolean;
  channelNames: string[];
  colorMap: Record<string, string>;
  testedNames: Set<string>;
  // True when the blended line is the only series on the chart. It then reads as the tooltip's
  // one item rather than as a footer summarising a channel list that isn't there.
  blendedIsolated: boolean;
}

// Channel name with a trailing beaker when the feature has an incrementality test.
const tooltipNameLabel = (name: string, tested: boolean): ReactNode => {
  if (!tested) return name;
  return (
    <span className='dtc-commerce-mmm-tooltip-name'>
      {name}
      <IncrementalityBeaker size={14} />
    </span>
  );
};

const headerFor = (label: string, timeAgg: TimeAgg): string => {
  if (timeAgg === TimeAgg.MONTHLY) {
    const [y, m] = label.split('-');
    return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  }
  if (timeAgg === TimeAgg.QUARTERLY) {
    const [y, q] = label.split('-');
    return `${q} ${y}`;
  }
  return new Date(`${label}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatEfficiencyValue = (value: number): string => formatDollarsExact(value);

// The shared tooltip drops any row with a falsy value, so an absent number needs a real string
// to survive — otherwise a period with nothing to report renders as an empty box.
const NO_VALUE_PLACEHOLDER = '—';

// Single row shown when no series on the chart has a value for the hovered period. Named for the
// isolated channel when there is one, so the tooltip says which series it is reporting on.
const emptyPeriodItem = (names: string[], colorMap: Record<string, string>): PayloadItem => ({
  label: names.length === 1 ? names[0] : 'No spend this period',
  value: NO_VALUE_PLACEHOLDER,
  color: names.length === 1 ? colorMap[names[0]] : Neutral600,
  keyShape: KeyShapeOptions.LINE
});

const CustomTooltip = ({
  active,
  payload,
  label,
  metric,
  timeAgg,
  displayMode,
  isRevenue,
  channelNames,
  colorMap,
  testedNames,
  blendedIsolated
}: CustomTooltipProps) => {
  if (!active || !payload?.length || !label) return null;
  const header = headerFor(label, timeAgg);

  if (metric === MetricView.EFFICIENCY) {
    const row = payload[0].payload;
    if (!row) return null;
    const entries: TooltipEntry[] = channelNames
      .map(name => {
        const value = row[`${name}_efficiency`];
        if (typeof value !== 'number' || value <= 0) return null;
        return { name, value, color: colorMap[name] };
      })
      .filter((e): e is TooltipEntry => e !== null)
      .sort((a, b) => (isRevenue ? b.value - a.value : a.value - b.value));
    const totalEff = row.totalEfficiency;
    const blendedItem: PayloadItem = {
      label: BLENDED_SERIES_LABEL,
      value: typeof totalEff === 'number' ? formatEfficiencyValue(totalEff) : NO_VALUE_PLACEHOLDER,
      color: Neutral900,
      // Matches the dashed stroke the blended line is drawn with.
      keyShape: KeyShapeOptions.DASHED_LINE
    };

    // Blended on its own: it is the series being read, so it belongs in the list. A footer here
    // would be a summary of an empty list.
    if (blendedIsolated) {
      return <ChartTooltipV2 headers={[header]} items={[blendedItem]} />;
    }

    const items: PayloadItem[] = entries.slice(0, 8).map(item => ({
      label: tooltipNameLabel(item.name, testedNames.has(item.name)),
      value: formatEfficiencyValue(item.value),
      color: item.color,
      keyShape: KeyShapeOptions.LINE
    }));
    return (
      <ChartTooltipV2
        headers={[header]}
        // A period where every shown series has nothing to report would otherwise render an
        // empty box; one dashed row keeps the tooltip saying something.
        items={items.length > 0 ? items : [emptyPeriodItem(channelNames, colorMap)]}
        footerLabels={[BLENDED_SERIES_LABEL]}
        footerValues={typeof totalEff === 'number' ? [formatEfficiencyValue(totalEff)] : []}
      />
    );
  }

  const showPercent = displayMode === DisplayMode.PERCENT;
  const entries: TooltipEntry[] = payload
    .filter(
      (p): p is Required<Pick<TooltipPayloadItem, 'name' | 'value'>> & TooltipPayloadItem =>
        typeof p.value === 'number' && p.value > 0 && Boolean(p.name)
    )
    .map(p => ({
      name: p.name,
      value: p.value,
      color: p.color || p.stroke || colorMap[p.name]
    }))
    .sort((a, b) => b.value - a.value);

  const total = entries.reduce((s, p) => s + p.value, 0);
  const fmtActual = (v: number) =>
    metric === MetricView.CONTRIBUTION && !isRevenue ? formatWholeNumber(v) : formatCurrency(v);
  const fmt = (v: number) => (showPercent ? formatPercentage(v, 1) : fmtActual(v));

  const visible = entries.slice(0, 8);
  const overflow = entries.length - visible.length;
  const items: PayloadItem[] = visible.map(item => ({
    label: tooltipNameLabel(item.name, testedNames.has(item.name)),
    value: fmt(item.value),
    color: item.color,
    keyShape: KeyShapeOptions.LINE
  }));
  if (overflow > 0) {
    items.push({
      label: `+${overflow} more`,
      value: ' ',
      color: 'transparent',
      keyShape: KeyShapeOptions.LINE
    });
  }

  return (
    <ChartTooltipV2
      headers={[header]}
      items={items}
      footerLabels={['Total']}
      footerValues={!showPercent ? [fmt(total)] : []}
    />
  );
};

export const ChannelPerformanceLine = ({ data, hierarchyLevel }: ChannelPerformanceLineProps) => {
  const { attributionType } = useDtcCommerceMmmData();
  const colorFor = useChannelColor();
  const isRevenue = data.modelInfo.kpiType === 'revenue';
  const [metric, setMetric] = useState<MetricView>(MetricView.SPEND);
  const [timeAgg, setTimeAgg] = useState<TimeAgg>(TimeAgg.WEEKLY);
  const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.ACTUAL);
  const [hoveredChannel, setHoveredChannel] = useState<string | null>(null);
  const [isolatedChannel, setIsolatedChannel] = useState<string | null>(null);
  const [showBaseline, setShowBaseline] = useState(false);

  const ad = data[attributionType];
  const isEfficiency = metric === MetricView.EFFICIENCY;
  const isContribution = metric === MetricView.CONTRIBUTION;
  const effectiveDisplayMode = isEfficiency ? DisplayMode.ACTUAL : displayMode;
  const showPercent = effectiveDisplayMode === DisplayMode.PERCENT;
  // Baseline has no spend and no efficiency — only show it as a tracked series for contribution.
  const baselineActive = showBaseline && isContribution;

  const { weeklyData, channelNames, colorMap, testedNames } = useMemo(() => {
    const allChannels = aggregateChannels(ad.cptbT12M, hierarchyLevel);
    const significant = allChannels
      .filter(ch => ch.effectiveNormalized > 0 || ch.rawSpend > 0)
      .sort((a, b) => b.effectiveNormalized - a.effectiveNormalized);

    const names = significant.map(ch => ch.displayName);
    const tested = new Set(
      significant.filter(ch => ch.hasIncrementalityTest).map(ch => ch.displayName)
    );

    const map: Record<string, string> = {};
    significant.forEach(ch => {
      map[ch.displayName] = colorFor(ch.channel);
    });

    const keyToName: Record<string, string> = {};
    significant.forEach(ch => {
      keyToName[ch.groupKey] = ch.displayName;
    });

    const baselineByDate = new Map<string, number>();
    if (baselineActive) {
      data.actualVsPredictedT12M.forEach(r => {
        baselineByDate.set(r.date, (baselineByDate.get(r.date) ?? 0) + r.baselinePrediction);
      });
      names.unshift(BASELINE_DISPLAY_NAME);
      map[BASELINE_DISPLAY_NAME] = Neutral500;
    }

    const rowsByDate = new Map<string, CPTBRow[]>();
    ad.cptbT12M.forEach(row => {
      const bucket = rowsByDate.get(row.date) ?? [];
      bucket.push(row);
      rowsByDate.set(row.date, bucket);
    });
    const dates = Array.from(rowsByDate.keys()).sort();

    const weekly: PeriodEntry[] = dates.map(date => {
      const weekRows = rowsByDate.get(date) ?? [];
      const entry: PeriodEntry = { date, totalEfficiency: null };
      names.forEach(name => {
        entry[`${name}_spend`] = 0;
        entry[`${name}_contribution`] = 0;
      });
      if (baselineActive) {
        entry[`${BASELINE_DISPLAY_NAME}_contribution`] = baselineByDate.get(date) ?? 0;
      }
      weekRows.forEach(row => {
        const gk = groupKeyFor(row, hierarchyLevel);
        const target = keyToName[gk];
        if (!target) return;
        entry[`${target}_spend`] = (entry[`${target}_spend`] as number) + row.rawSpend;
        entry[`${target}_contribution`] =
          (entry[`${target}_contribution`] as number) + row.effectiveNormalized;
      });
      let totalSpend = 0;
      let totalContrib = 0;
      names.forEach(name => {
        const s = entry[`${name}_spend`] as number;
        const c = entry[`${name}_contribution`] as number;
        let eff: number | null = null;
        if (isRevenue && s > 0) {
          eff = c / s;
        } else if (!isRevenue && c > 0) {
          eff = s / c;
        }
        entry[`${name}_efficiency`] = eff;
        totalSpend += s;
        totalContrib += c;
      });
      if (isRevenue && totalSpend > 0) {
        entry.totalEfficiency = totalContrib / totalSpend;
      } else if (!isRevenue && totalContrib > 0) {
        entry.totalEfficiency = totalSpend / totalContrib;
      }
      return entry;
    });

    return { weeklyData: weekly, channelNames: names, colorMap: map, testedNames: tested };
  }, [ad, data.actualVsPredictedT12M, hierarchyLevel, isRevenue, baselineActive, colorFor]);

  const periodData = useMemo<PeriodEntry[]>(() => {
    const base =
      timeAgg === TimeAgg.WEEKLY
        ? weeklyData.map(entry => ({ ...entry }))
        : (() => {
            const buckets = new Map<string, PeriodEntry>();
            weeklyData.forEach(entry => {
              const key = periodKey(entry.date, timeAgg);
              const existing = buckets.get(key);
              if (!existing) {
                const fresh: PeriodEntry = { date: key, totalEfficiency: null };
                channelNames.forEach(name => {
                  fresh[`${name}_spend`] = 0;
                  fresh[`${name}_contribution`] = 0;
                });
                channelNames.forEach(name => {
                  fresh[`${name}_spend`] =
                    (fresh[`${name}_spend`] as number) + (entry[`${name}_spend`] as number);
                  fresh[`${name}_contribution`] =
                    (fresh[`${name}_contribution`] as number) +
                    (entry[`${name}_contribution`] as number);
                });
                buckets.set(key, fresh);
                return;
              }
              channelNames.forEach(name => {
                existing[`${name}_spend`] =
                  (existing[`${name}_spend`] as number) + (entry[`${name}_spend`] as number);
                existing[`${name}_contribution`] =
                  (existing[`${name}_contribution`] as number) +
                  (entry[`${name}_contribution`] as number);
              });
            });
            return Array.from(buckets.values())
              .map(bucket => {
                const enriched: PeriodEntry = { ...bucket };
                let totalSpend = 0;
                let totalContrib = 0;
                channelNames.forEach(name => {
                  const s = enriched[`${name}_spend`] as number;
                  const c = enriched[`${name}_contribution`] as number;
                  let eff: number | null = null;
                  if (isRevenue && s > 0) {
                    eff = c / s;
                  } else if (!isRevenue && c > 0) {
                    eff = s / c;
                  }
                  enriched[`${name}_efficiency`] = eff;
                  totalSpend += s;
                  totalContrib += c;
                });
                if (isRevenue && totalSpend > 0) {
                  enriched.totalEfficiency = totalContrib / totalSpend;
                } else if (!isRevenue && totalContrib > 0) {
                  enriched.totalEfficiency = totalSpend / totalContrib;
                }
                return enriched;
              })
              .sort((a, b) => a.date.localeCompare(b.date));
          })();

    return base.map(entry => {
      let spendTotal = 0;
      let contribTotal = 0;
      channelNames.forEach(name => {
        spendTotal += entry[`${name}_spend`] as number;
        contribTotal += entry[`${name}_contribution`] as number;
      });
      const withPct: PeriodEntry = { ...entry };
      channelNames.forEach(name => {
        const s = entry[`${name}_spend`] as number;
        const c = entry[`${name}_contribution`] as number;
        withPct[`${name}_spend_pct`] = spendTotal > 0 ? (s / spendTotal) * 100 : 0;
        withPct[`${name}_contribution_pct`] = contribTotal > 0 ? (c / contribTotal) * 100 : 0;
      });
      return withPct;
    });
  }, [weeklyData, timeAgg, channelNames, isRevenue]);

  const suffix = (() => {
    if (metric === MetricView.EFFICIENCY) return '_efficiency';
    const base = metric === MetricView.SPEND ? '_spend' : '_contribution';
    return showPercent ? `${base}_pct` : base;
  })();

  const yearBoundaries = useMemo(
    () =>
      findYearBoundaries(
        periodData.map(p => p.date),
        timeAgg
      ),
    [periodData, timeAgg]
  );

  const { ref: chartRef, width: chartWidth } = useElementWidth();
  const LINE_MARGIN_LEFT = 4;
  const LINE_MARGIN_RIGHT = 16;
  const LINE_Y_AXIS_WIDTH = 60;

  const xTickFormatter = (key: string) => {
    if (timeAgg === TimeAgg.WEEKLY) {
      return new Date(`${key}T00:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
    if (timeAgg === TimeAgg.MONTHLY) {
      const [y, m] = key.split('-');
      return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short' });
    }
    return key;
  };

  const yTickFormatter = (v: number): string => {
    if (isEfficiency) return formatEfficiencyValue(v);
    if (showPercent) return formatPercentage(v, 0);
    if (metric === MetricView.CONTRIBUTION && !isRevenue) return formatWholeNumber(v);
    return formatCurrency(v);
  };

  const metricOptions = [
    { value: MetricView.SPEND, label: 'Spend' },
    { value: MetricView.CONTRIBUTION, label: isRevenue ? 'Revenue' : 'Conversions' },
    { value: MetricView.EFFICIENCY, label: isRevenue ? 'ROAS' : 'CPA' }
  ];

  const timeOptions = [
    { value: TimeAgg.WEEKLY, label: 'Weekly' },
    { value: TimeAgg.MONTHLY, label: 'Monthly' },
    { value: TimeAgg.QUARTERLY, label: 'Quarterly' }
  ];

  const displayOptions = [
    { value: DisplayMode.ACTUAL, label: 'Actual Values' },
    { value: DisplayMode.PERCENT, label: 'Percent' }
  ];

  const blendedIsolated = isolatedChannel === BLENDED_SERIES_KEY;
  // Isolating the blended line leaves no channel lines at all; isolating a channel drops the
  // blended line, matching how isolating one channel drops the others.
  const renderedChannels = (() => {
    if (blendedIsolated) return [];
    if (isolatedChannel) return [isolatedChannel];
    return channelNames;
  })();
  const showBlendedLine = isEfficiency && (!isolatedChannel || blendedIsolated);
  const yDomain: [number | string, number | string] | undefined =
    showPercent && !isolatedChannel ? [0, 100] : undefined;

  const opacityFor = (name: string) => {
    if (isolatedChannel) return 1;
    if (hoveredChannel && hoveredChannel !== name) return FADED_OPACITY;
    return 1;
  };

  const fillOpacityFor = (name: string) => {
    if (isolatedChannel) return NORMAL_FILL_OPACITY;
    if (hoveredChannel && hoveredChannel !== name) return FADED_OPACITY;
    return NORMAL_FILL_OPACITY;
  };

  const handleLegendClick = (name: string) => {
    setIsolatedChannel(prev => (prev === name ? null : name));
    // The pointer is still sitting on the item that was just clicked, and the mouse-leave
    // handler ignores the exit while something is isolated. Without clearing here, the stale
    // hover outlives the isolation and dims every series the moment isolation is released.
    setHoveredChannel(null);
  };

  // Blended only exists in the efficiency view, so an isolation on it can't survive a move to
  // spend or contribution — drop it rather than leaving an empty chart behind.
  const handleMetricChange = (next: MetricView) => {
    if (next !== MetricView.EFFICIENCY && isolatedChannel === BLENDED_SERIES_KEY) {
      setIsolatedChannel(null);
    }
    // A view arrived at fresh should look untouched, so no hover carries across the switch.
    setHoveredChannel(null);
    setMetric(next);
  };

  const handleLegendMouseEnter = (name: string) => {
    if (isolatedChannel) return;
    setHoveredChannel(name);
  };

  const handleLegendMouseLeave = () => {
    if (isolatedChannel) return;
    setHoveredChannel(null);
  };

  if (channelNames.length === 0 || periodData.length === 0) {
    return (
      <div className='channel-performance-line'>
        <div className='channel-performance-line-empty'>No data in the selected window.</div>
      </div>
    );
  }

  return (
    <div className='channel-performance-line'>
      <div className='channel-performance-line-toolbar'>
        <div className='channel-performance-line-controls'>
          <Dropdown<MetricView>
            overlayPlacement={Placement.RIGHT}
            size={Size.SM}
            value={metric}
            options={metricOptions}
            onChange={handleMetricChange}
          />
          <Dropdown<TimeAgg>
            overlayPlacement={Placement.RIGHT}
            size={Size.SM}
            value={timeAgg}
            options={timeOptions}
            onChange={setTimeAgg}
          />
          <Dropdown<DisplayMode>
            overlayPlacement={Placement.RIGHT}
            size={Size.SM}
            value={displayMode}
            options={displayOptions}
            onChange={setDisplayMode}
            disabled={isEfficiency}
            dropdownMenuClassName='align-right'
          />
        </div>
      </div>

      <div className='channel-performance-line-chart' ref={chartRef}>
        <ResponsiveContainer width='100%' height={520}>
          {isEfficiency ? (
            <ComposedChart
              data={periodData}
              margin={{
                top: 24,
                right: LINE_MARGIN_RIGHT,
                bottom: 8,
                left: LINE_MARGIN_LEFT
              }}
            >
              <XAxis
                dataKey='date'
                tickFormatter={xTickFormatter}
                tick={{ fontSize: 10, fill: Neutral600 }}
                axisLine={false}
                tickLine={false}
                minTickGap={timeAgg === TimeAgg.WEEKLY ? 60 : 40}
              />
              <YAxis
                tickFormatter={yTickFormatter}
                tick={{ fontSize: 10, fill: Neutral600 }}
                axisLine={false}
                tickLine={false}
                width={LINE_Y_AXIS_WIDTH}
              />
              {yearBoundaries.map(({ dateKey, year }) => (
                <ReferenceLine
                  key={`yr-${dateKey}`}
                  x={dateKey}
                  stroke={YEAR_MARKER_STROKE}
                  strokeDasharray='4 4'
                  strokeWidth={1}
                  label={
                    <YearMarkerLabel
                      year={year}
                      chartWidth={chartWidth}
                      marginLeft={LINE_MARGIN_LEFT}
                      marginRight={LINE_MARGIN_RIGHT}
                      yAxisWidth={LINE_Y_AXIS_WIDTH}
                    />
                  }
                />
              ))}
              <Tooltip
                content={
                  <CustomTooltip
                    metric={metric}
                    timeAgg={timeAgg}
                    displayMode={effectiveDisplayMode}
                    isRevenue={isRevenue}
                    channelNames={renderedChannels}
                    colorMap={colorMap}
                    testedNames={testedNames}
                    blendedIsolated={blendedIsolated}
                  />
                }
                cursor={{ stroke: Neutral500, strokeWidth: 1, strokeDasharray: '4 3' }}
              />
              {renderedChannels.map(name => (
                <Line
                  key={name}
                  type='monotone'
                  dataKey={`${name}_efficiency`}
                  name={name}
                  stroke={colorMap[name]}
                  strokeWidth={1.5}
                  strokeOpacity={opacityFor(name)}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
              {showBlendedLine && (
                <Line
                  type='monotone'
                  dataKey='totalEfficiency'
                  name={BLENDED_SERIES_LABEL}
                  stroke={Neutral900}
                  strokeWidth={2.5}
                  strokeDasharray='6 3'
                  strokeOpacity={opacityFor(BLENDED_SERIES_KEY)}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          ) : (
            <AreaChart
              data={periodData}
              margin={{
                top: 24,
                right: LINE_MARGIN_RIGHT,
                bottom: 8,
                left: LINE_MARGIN_LEFT
              }}
            >
              <XAxis
                dataKey='date'
                tickFormatter={xTickFormatter}
                tick={{ fontSize: 10, fill: Neutral600 }}
                axisLine={false}
                tickLine={false}
                minTickGap={timeAgg === TimeAgg.WEEKLY ? 60 : 40}
              />
              <YAxis
                tickFormatter={yTickFormatter}
                tick={{ fontSize: 10, fill: Neutral600 }}
                axisLine={false}
                tickLine={false}
                width={LINE_Y_AXIS_WIDTH}
                domain={yDomain}
              />
              {yearBoundaries.map(({ dateKey, year }) => (
                <ReferenceLine
                  key={`yr-${dateKey}`}
                  x={dateKey}
                  stroke={YEAR_MARKER_STROKE}
                  strokeDasharray='4 4'
                  strokeWidth={1}
                  label={
                    <YearMarkerLabel
                      year={year}
                      chartWidth={chartWidth}
                      marginLeft={LINE_MARGIN_LEFT}
                      marginRight={LINE_MARGIN_RIGHT}
                      yAxisWidth={LINE_Y_AXIS_WIDTH}
                    />
                  }
                />
              ))}
              <Tooltip
                content={
                  <CustomTooltip
                    metric={metric}
                    timeAgg={timeAgg}
                    displayMode={effectiveDisplayMode}
                    isRevenue={isRevenue}
                    channelNames={renderedChannels}
                    colorMap={colorMap}
                    testedNames={testedNames}
                    blendedIsolated={blendedIsolated}
                  />
                }
                cursor={{ stroke: Neutral500, strokeWidth: 1, strokeDasharray: '4 3' }}
              />
              {renderedChannels.map(name => (
                <Area
                  key={name}
                  type='monotone'
                  dataKey={`${name}${suffix}`}
                  name={name}
                  stackId={isolatedChannel ? undefined : '1'}
                  fill={colorMap[name]}
                  stroke={colorMap[name]}
                  fillOpacity={fillOpacityFor(name)}
                  strokeOpacity={opacityFor(name)}
                  strokeWidth={0}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className='channel-performance-line-legend'>
        {channelNames.map(name => (
          <InteractiveDiv
            key={name}
            className={cn('channel-performance-line-legend-item', {
              isolated: isolatedChannel === name,
              dimmed: isolatedChannel !== null && isolatedChannel !== name
            })}
            onClick={() => handleLegendClick(name)}
            onMouseEnter={() => handleLegendMouseEnter(name)}
            onMouseLeave={handleLegendMouseLeave}
          >
            <span
              className='channel-performance-line-legend-swatch'
              style={{ backgroundColor: colorMap[name] }}
            />
            <span>{name}</span>
            {testedNames.has(name) && <IncrementalityBeaker />}
          </InteractiveDiv>
        ))}
        {isEfficiency && (
          <InteractiveDiv
            className={cn('channel-performance-line-legend-item', {
              isolated: blendedIsolated,
              dimmed: isolatedChannel !== null && !blendedIsolated
            })}
            onClick={() => handleLegendClick(BLENDED_SERIES_KEY)}
            onMouseEnter={() => handleLegendMouseEnter(BLENDED_SERIES_KEY)}
            onMouseLeave={handleLegendMouseLeave}
          >
            <span className='channel-performance-line-legend-swatch dashed' />
            <span>{BLENDED_SERIES_LABEL}</span>
          </InteractiveDiv>
        )}
      </div>

      <div className='channel-performance-line-footer'>
        <IncrementalityNote />
        <ToggleSwitch
          label={BASELINE_TOGGLE_LABEL}
          checked={showBaseline}
          onChange={setShowBaseline}
          disabled={!isContribution}
        />
      </div>
    </div>
  );
};
